import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import AnimatedCard from '../components/AnimatedCard';
import UnlockSmart1 from "../assets/images/blog1.jpg";
import UnlockSmart2 from "../assets/images/blog2.jpg";
import UnlockSmart3 from "../assets/images/blog1.jpg";
import UnlockSmart4 from "../assets/images/blog2.jpg";

const Blog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeBlog, setActiveBlog] = useState(null);
  
  // Blog data - typically would come from an API or database
  const blogs = [
    {
      id: 1,
      title: "Nigeria Leads the Way: Revolutionizing Education with Bildup AI",
      description: "Innovation often begins with a simple question. For me, that question arose unexpectedly during a visit to my brother...",
      content: `
        <p>Innovation often begins with a simple question. For me, that question arose unexpectedly during a visit to my brother: how could I, amid my demanding schedule, guide my niece and nephew on their career paths in a meaningful way? That question led to an idea—a spark—that has grown into a groundbreaking innovation poised to redefine education across Africa and beyond.</p>
        
        <p>At the time, my team was tackling a challenge close to our hearts. We had recently launched an apprenticeship program aimed at empowering learners, but providing personalized support to each beneficiary proved difficult. We wanted a fresh start with foundational knowledge, yet advanced learners found this tedious, while beginners struggled to keep up when we started midway. Neither recorded lessons nor live online sessions could provide the tailored learning experience we envisioned.</p>
        
        <p>Then came the turning point: what if there was a solution—a super-intelligent AI tutor—not only for mentoring my niece and nephew but for delivering personalized, impactful learning experiences to anyone? That idea gave birth to Bildup AI.</p>
        
        
      `,
      author: "C.E.O Bildup AI",
      date: "2023-11-15",
      category: "Education Technology",
      readTime: "2 min read",
      image: UnlockSmart2,
      featured: true
    },

  ];
  
  // Sort blogs by date (newest first)
  const sortedBlogs = [...blogs].sort((a, b) => new Date(b.date) - new Date(a.date));
  
  // Find featured blogs
  const featuredBlogs = sortedBlogs.filter(blog => blog.featured);
  
  // Find blog by ID when URL has an ID parameter
  useEffect(() => {
    if (id) {
      const foundBlog = blogs.find(blog => blog.id === parseInt(id));
      if (foundBlog) {
        setActiveBlog(foundBlog);
        // Scroll to top when viewing a single blog
        window.scrollTo(0, 0);
      } else {
        // Redirect to blog listing if ID not found
        navigate("/blog");
      }
    } else {
      setActiveBlog(null);
    }
  }, [id, navigate]);
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Handle navigation to a specific blog
  const navigateToBlog = (blogId) => {
    navigate(`/blog/${blogId}`);
  };
  
  return (
    <div className="bg-gray-50 min-h-screen pt-16 pb-20">
      <div className="max-w-screen-xl mx-auto px-4">
        {activeBlog ? (
          // Single blog view
          <div className="max-w-3xl mx-auto">
            <button 
              onClick={() => navigate('/blog')}
              className="flex items-center text-primary-purple-400 mb-6 hover:underline"
            >
              <Icon icon="mdi:arrow-left" className="mr-2" />
              Back to all articles
            </button>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <img 
                  src={activeBlog.image} 
                  alt={activeBlog.title} 
                  className="w-full h-64 object-cover"
                />
                
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span>{activeBlog.category}</span>
                    <span className="mx-2">•</span>
                    <span>{formatDate(activeBlog.date)}</span>
                    <span className="mx-2">•</span>
                    <span>{activeBlog.readTime}</span>
                  </div>
                  
                  <h1 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
                    {activeBlog.title}
                  </h1>
                  
                  <div className="flex items-center mb-8">
                    <div className="w-10 h-10 bg-primary-purple-200 rounded-full flex items-center justify-center text-white font-bold">
                      {activeBlog.author.split(' ').map(name => name[0]).join('')}
                    </div>
                    <span className="ml-3 text-gray-700">{activeBlog.author}</span>
                  </div>
                  
                  <div 
                    className="prose prose-lg max-w-none prose-headings:text-primary-700 prose-a:text-primary-purple-400 text-center flex flex-col gap-6"
                    dangerouslySetInnerHTML={{ __html: activeBlog.content }}
                  />
                  
                  <div className="mt-12 pt-6 border-t border-gray-200">
                    <h3 className="text-xl font-bold mb-4">Share this article</h3>
                    <div className="flex gap-4">
                      <button className="p-2 bg-blue-100 rounded-full text-blue-600 hover:bg-blue-200">
                        <Icon icon="mdi:twitter" width={24} height={24} />
                      </button>
                      <button className="p-2 bg-blue-100 rounded-full text-blue-800 hover:bg-blue-200">
                        <Icon icon="mdi:facebook" width={24} height={24} />
                      </button>
                      <button className="p-2 bg-blue-100 rounded-full text-blue-500 hover:bg-blue-200">
                        <Icon icon="mdi:linkedin" width={24} height={24} />
                      </button>
                      <button className="p-2 bg-blue-100 rounded-full text-gray-600 hover:bg-blue-200">
                        <Icon icon="mdi:link-variant" width={24} height={24} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        ) : (
          // Blog listing view
          <>
            <div className="text-center mb-16">
              <AnimatedCard
                direction="fadeIn"
                duration={0.8}
                once={true}
                className="flex flex-col items-center justify-center gap-4"
              >
                <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                  Bildup AI Blog
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Insights, updates, and stories about AI in education and how we're transforming learning experiences.
                </p>
              </AnimatedCard>
            </div>
            
            {/* Featured Posts Section */}
            {featuredBlogs.length > 0 && (
              <div className="mb-16">
                <h2 className="text-2xl font-bold mb-8 text-gray-800">Featured Articles</h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {featuredBlogs.map((blog, index) => (
                    <AnimatedCard
                      key={blog.id}
                      direction={index % 2 === 0 ? "left" : "right"}
                      delay={0.1 * index}
                      className="h-full"
                    >
                      <motion.div 
                        className="bg-white rounded-xl shadow-md overflow-hidden h-full flex flex-col cursor-pointer hover:shadow-lg transition-shadow duration-300"
                        whileHover={{ y: -5 }}
                        onClick={() => navigateToBlog(blog.id)}
                      >
                        <img 
                          src={blog.image} 
                          alt={blog.title} 
                          className="w-full h-48 object-cover"
                        />
                        
                        <div className="p-6 flex-1 flex flex-col">
                          <div className="flex items-center text-sm text-gray-500 mb-2">
                            <span className="bg-primary-purple-100 text-primary-purple-600 px-3 py-1 rounded-full">
                              {blog.category}
                            </span>
                            <span className="ml-3">{formatDate(blog.date)}</span>
                          </div>
                          
                          <h3 className="text-xl font-bold mb-3 text-gray-900">
                            {blog.title}
                          </h3>
                          
                          <p className="text-gray-600 mb-4 flex-1">
                            {blog.description}
                          </p>
                          
                          <div className="flex items-center justify-between mt-auto">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-primary-purple-200 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                {blog.author.split(' ').map(name => name[0]).join('')}
                              </div>
                              <span className="ml-2 text-sm text-gray-700">{blog.author}</span>
                            </div>
                            <span className="text-sm text-gray-500">{blog.readTime}</span>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatedCard>
                  ))}
                </div>
              </div>
            )}
            
            {/* All Posts Section */}
            <div>
              <h2 className="text-2xl font-bold mb-8 text-gray-800">All Articles</h2>
              
              <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-6">
                {sortedBlogs.map((blog, index) => (
                  <AnimatedCard
                    key={blog.id}
                    direction="bottom"
                    delay={0.05 * index}
                    className="h-full"
                  >
                    <motion.div 
                      className="bg-white rounded-lg shadow-sm overflow-hidden h-full flex flex-col cursor-pointer hover:shadow-md transition-shadow duration-300"
                      whileHover={{ y: -3 }}
                      onClick={() => navigateToBlog(blog.id)}
                    >
                      <img 
                        src={blog.image} 
                        alt={blog.title} 
                        className="w-full h-40 object-cover"
                      />
                      
                      <div className="p-5 flex-1 flex flex-col">
                        <div className="flex items-center text-xs text-gray-500 mb-2">
                          <span>{blog.category}</span>
                          <span className="mx-2">•</span>
                          <span>{formatDate(blog.date)}</span>
                        </div>
                        
                        <h3 className="text-lg font-semibold mb-2 text-gray-900">
                          {blog.title}
                        </h3>
                        
                        <p className="text-sm text-gray-600 mb-3 flex-1">
                          {blog.description}
                        </p>
                        
                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                          <span className="text-xs text-gray-500">{blog.readTime}</span>
                          <span className="text-primary-purple-400 text-sm font-medium flex items-center">
                            Read more
                            <Icon icon="mdi:arrow-right" className="ml-1" width={16} />
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatedCard>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Blog; 